import React from 'react';
import { glassSurface } from './config/glassTheme';
import ProductNav from './components/ProductNav';
import {
  COMBAT_TIERS, TIER_ORDER, COMPARISON_ROWS, SPEC_ROWS, getTierSpec,
  COMBAT_PRO_BASE_PRICE, COMBAT_PRO_BASE_INCLUDES,
  formatTierPrice, tierRank,
} from './combatProTiers';

// Shared VISINVOKE-styled building blocks for the Combat PRO account pages.
// Matches the existing dashboard card language (dark purple cards, gradients).
// All tier values come from TIER_SPECS / SPEC_ROWS in combatProTiers.js.

export const cps = {
  page:        { minHeight: '100vh', background: 'linear-gradient(135deg, #1a1840 0%, #2d3a4a 50%, #701030 100%)', padding: '0 0 60px 0', color: '#fff', fontFamily: "'Segoe UI', sans-serif" },
  wrap:        { maxWidth: 1400, margin: '0 auto', padding: '28px 40px' },
  backLink:    { ...glassSurface('#334155', '#64748B', '#94A3B8'), borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  hero:        { padding: '18px 0 18px 0' },
  backBtn:     { ...glassSurface('#334155', '#64748B', '#94A3B8'), borderRadius: 8, padding: '9px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  heroTitle:   { fontSize: 32, fontWeight: 800, margin: 0, lineHeight: 1.1 },
  heroSub:     { fontSize: 15, color: 'rgba(255,255,255,0.6)', marginTop: 8 },
  sectionTitle:{ fontSize: 14, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', margin: '24px 0 12px 0' },

  card:        { background: 'rgba(30,24,70,0.88)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: '24px 24px', display: 'flex', flexDirection: 'column', gap: 12 },
  cardLight:   { background: 'rgba(255,255,255,0.97)', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 16, padding: '24px', color: '#1a1a1a', boxShadow: '0 4px 20px rgba(0,0,0,0.09)' },

  baseCard:    { background: 'rgba(30,24,70,0.92)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 18, padding: '28px', boxShadow: '0 0 0 1px rgba(245,158,11,0.15), 0 8px 30px rgba(0,0,0,0.35)' },
  baseHead:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 },
  baseTitle:   { fontSize: 22, fontWeight: 800, color: '#fbbf24' },
  basePrice:   { fontSize: 32, fontWeight: 800, color: '#fff', lineHeight: 1.1 },
  basePriceUnit:{ fontSize: 15, color: 'rgba(255,255,255,0.55)', fontWeight: 700 },
  baseStatus:  { padding: '8px 16px', borderRadius: 10, fontSize: 14, fontWeight: 800, alignSelf: 'center' },

  // ── Current Plan (full-width) ──
  currentPlanCard: { background: 'rgba(30,24,70,0.92)', border: '2px solid', borderRadius: 18, padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 6 },
  cpHeader:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' },
  cpKicker:    { fontSize: 12, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' },
  cpBadge:     { padding: '6px 18px', borderRadius: 999, fontSize: 18, fontWeight: 800 },
  cpPrice:     { fontSize: 20, fontWeight: 800, color: '#fff' },
  cpDivider:   { height: 1, background: 'rgba(255,255,255,0.1)', width: '100%' },

  // ── Tier cards ──
  tierGrid:    { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, alignItems: 'stretch' },
  tierGrid3:   { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, alignItems: 'stretch' },
  tierCard:    { background: 'rgba(30,24,70,0.88)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: '20px', display: 'flex', flexDirection: 'column', gap: 10, height: '100%' },
  tierName:    { fontSize: 18, fontWeight: 800, color: '#fff' },
  tierPrice:   { fontSize: 26, fontWeight: 800, lineHeight: 1.1, marginBottom: 4 },
  tierPer:     { fontSize: 14, color: 'rgba(255,255,255,0.55)', fontWeight: 700 },

  // ── Hero stats (Saved Encounters) ──
  heroStats:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, margin: '2px 0 6px' },
  heroStat:    { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '12px 10px', textAlign: 'center' },
  heroNum:     { fontSize: 26, fontWeight: 800, color: '#fff', lineHeight: 1 },
  heroLbl:     { fontSize: 11.5, fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginTop: 5, textTransform: 'uppercase', letterSpacing: 0.5 },

  // ── Spec rows ──
  specWrap:    { display: 'flex', flexDirection: 'column', gap: 0, flex: 1 },
  specRow:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  specLabel:   { fontSize: 12.5, color: 'rgba(255,255,255,0.62)', fontWeight: 600 },
  specVal:     { fontSize: 13, color: '#fff', fontWeight: 700, textAlign: 'right' },

  tick:        { color: '#4ade80', fontWeight: 800, flexShrink: 0 },
  cross:       { color: 'rgba(255,255,255,0.3)', fontWeight: 800 },

  btn:         { ...glassSurface('#334155', '#64748B', '#94A3B8'), width: '100%', borderRadius: 9, padding: '12px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer', textAlign: 'center' },
  btnGold:     { ...glassSurface('#A66D05', '#f59e0b', '#FBBF24') },
  btnGhost:    { ...glassSurface('#334155', '#64748B', '#94A3B8') },
  currentTag:  { ...glassSurface('#0E7A50', '#4ade80', '#6EE7B7'), textAlign: 'center', borderRadius: 9, padding: '11px 0', fontWeight: 800, letterSpacing: 1, fontSize: 13 },

  badge:       { display: 'inline-block', padding: '5px 13px', borderRadius: 999, fontSize: 13, fontWeight: 800 },

  // ── Summary cards (purchase history top row) ──
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 8 },
  summaryCard: { background: 'rgba(30,24,70,0.88)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: '18px', textAlign: 'center' },
  summaryVal:  { fontSize: 22, fontWeight: 800, lineHeight: 1.15 },
  summaryLbl:  { fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.55)', marginTop: 6 },

  table:       { width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 640 },
  tableWrap:   { overflowX: 'auto', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12 },
  th:          { padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', fontWeight: 800, fontSize: 12.5, textTransform: 'uppercase', letterSpacing: 0.4 },
  td:          { padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.85)' },
  rowLabel:    { textAlign: 'left', fontWeight: 700, color: '#fff' },
};

export function tierColor(tierKey) {
  return (COMBAT_TIERS[tierKey] && COMBAT_TIERS[tierKey].color) || '#6b7280';
}

function specValueNode(v) {
  if (v === true) return <span style={{ color: '#4ade80', fontWeight: 800 }}>Yes</span>;
  if (v === false) return <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 700 }}>No</span>;
  return <span>{String(v)}</span>;
}

export function SpecRows({ tierKey, columns }) {
  const spec = getTierSpec(tierKey);
  const multi = columns && columns > 1;
  const containerStyle = multi
    ? { display: 'grid', gridTemplateColumns: 'repeat(' + columns + ', minmax(0, 1fr))', gap: '0 26px' }
    : cps.specWrap;
  return (
    <div style={containerStyle}>
      {SPEC_ROWS.map((r) => (
        <div key={r.key} style={cps.specRow}>
          <span style={cps.specLabel}>{r.label}</span>
          <span style={cps.specVal}>{specValueNode(spec[r.key])}</span>
        </div>
      ))}
    </div>
  );
}

export function HeroStats({ tierKey }) {
  const spec = getTierSpec(tierKey);
  return (
    <div style={{ ...cps.heroStats, gridTemplateColumns: '1fr' }}>
      <div style={cps.heroStat}>
        <div style={cps.heroNum}>{spec.savedEncounters}</div>
        <div style={cps.heroLbl}>Prepared Encounters</div>
      </div>
    </div>
  );
}

export function TierBadge({ tierKey }) {
  const d = COMBAT_TIERS[tierKey] || COMBAT_TIERS.none;
  return <span style={{ ...cps.badge, background: d.color + '26', color: d.color, border: '1px solid ' + d.color + '80' }}>{d.name}</span>;
}

/**
 * `backTo` / `backLabel` default to Combat PRO, which is correct for the four Combat PRO pages that
 * use this shell. The standalone generator catalogue is deliberately NOT part of Combat PRO — its
 * customer path is Dashboard -> Gaming -> D&D & RPG Generators -> catalogue — so it passes its own
 * parent instead of inheriting a relationship that does not exist.
 */
export function CombatProShell({ title, subtitle, navigate, children,
  backTo = '/dashboard/battletracker', backLabel = 'Back to Combat PRO' }) {
  return (
    <div style={cps.page}>
      <ProductNav />
      <div style={cps.wrap}>
        <button style={cps.backLink} onClick={() => navigate(backTo)}>&larr; {backLabel}</button>
        <div style={cps.hero}>
          <h1 style={cps.heroTitle}>{title}</h1>
          {subtitle ? <div style={cps.heroSub}>{subtitle}</div> : null}
        </div>
        {children}
      </div>
    </div>
  );
}

export function BaseOwnershipCard({ owned, onPurchase, purchasing }) {
  return (
    <div style={cps.baseCard}>
      <div style={cps.baseHead}>
        <div>
          <div style={cps.baseTitle}>Combat PRO</div>
          <div style={cps.basePrice}>${COMBAT_PRO_BASE_PRICE} <span style={cps.basePriceUnit}>USD</span></div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>One-Time Purchase</div>
        </div>
        <div style={{ ...cps.baseStatus, ...(owned ? { background: 'rgba(74,222,128,0.16)', color: '#4ade80' } : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }) }}>
          {owned ? 'Owned \u2713' : 'Not Owned'}
        </div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', margin: '12px 0 4px 0' }}>Includes</div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {COMBAT_PRO_BASE_INCLUDES.map((f, i) => (
          <li key={i} style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.85)', display: 'flex', gap: 8, lineHeight: 1.4 }}><span style={cps.tick}>&#10003;</span>{f}</li>
        ))}
      </ul>
      <div style={{ maxWidth: 360, marginTop: 8 }}>
        {owned
          ? <div style={cps.currentTag}>OWNED</div>
          : <button style={{ ...cps.btn, ...cps.btnGold, opacity: purchasing ? 0.6 : 1 }} onClick={onPurchase} disabled={purchasing}>{purchasing ? 'Starting...' : 'Purchase Combat PRO'}</button>}
      </div>
    </div>
  );
}

export function TierCard({ tierKey, currentTier, onChoose, busy }) {
  const d = COMBAT_TIERS[tierKey];
  const isCurrent = currentTier === tierKey;
  let action = <div style={cps.currentTag}>CURRENT PLAN</div>;
  if (!isCurrent && tierKey === 'none') {
    action = <div style={{ ...cps.currentTag, color: 'rgba(255,255,255,.6)' }}>LOCAL ONLY</div>;
  } else if (!isCurrent) {
    action = <button disabled={busy || !onChoose} style={{ ...cps.btn, ...cps.btnGold, opacity: (busy || !onChoose) ? .55 : 1 }} onClick={() => onChoose(tierKey)}>
      Choose {d.name}
    </button>;
  }
  return (
    <div style={{ ...cps.tierCard, ...(isCurrent ? { border: '1px solid ' + d.color, boxShadow: '0 0 0 1px ' + d.color + ', 0 0 26px ' + d.color + '40' } : {}) }}>
      <div style={cps.tierName}>{d.name}</div>
      <div style={{ ...cps.tierPrice, color: d.color }}>{d.price ? <>${d.price.toFixed(2)}<span style={cps.tierPer}>{d.per}</span></> : 'Included locally'}</div>
      <HeroStats tierKey={tierKey} />
      <SpecRows tierKey={tierKey} />
      <div style={{ marginTop: 'auto', paddingTop: 12 }}>{action}</div>
    </div>
  );
}
export function CurrentPlanCard({ tierKey, owned, ctaLabel, onCta }) {
  const d = COMBAT_TIERS[tierKey] || COMBAT_TIERS.none;
  const cloud = tierKey !== 'none';
  return (
    <div style={{ ...cps.currentPlanCard, borderColor: d.color, boxShadow: '0 0 0 1px ' + d.color + '66, 0 10px 34px rgba(0,0,0,0.4)' }}>
      <div style={cps.cpHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={cps.cpKicker}>Current Plan</span>
          <span style={{ ...cps.cpBadge, background: d.color + '26', color: d.color, border: '1px solid ' + d.color + '99' }}>{d.name}</span>
          <span style={cps.cpPrice}>{d.price ? ('$' + d.price.toFixed(2) + d.per) : 'Free'}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: cloud ? '#4ade80' : 'rgba(255,255,255,0.5)' }}>{cloud ? 'Cloud Active' : 'Cloud Local only'}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: owned ? '#4ade80' : 'rgba(255,255,255,0.6)' }}>{owned ? 'Combat PRO Owned' : 'Combat PRO Not Owned'}</span>
        </div>
        {ctaLabel ? <button style={{ ...cps.btn, ...cps.btnGold, width: 'auto', padding: '11px 22px' }} onClick={onCta}>{ctaLabel}</button> : null}
      </div>
      <div style={cps.cpDivider} />
      <HeroStats tierKey={tierKey} />
      <SpecRows tierKey={tierKey} columns={3} />
    </div>
  );
}

export function ComparisonTable({ currentTier }) {
  const cols = TIER_ORDER;
  const cell = (v) => v === true
    ? <span style={cps.tick}>&#10003;</span>
    : (v === false ? <span style={cps.cross}>&#10007;</span> : String(v));
  return (
    <div style={cps.tableWrap}>
      <table style={cps.table}>
        <thead>
          <tr>
            <th style={{ ...cps.th, textAlign: 'left' }}>Feature</th>
            {cols.map((t) => (
              <th key={t} style={{ ...cps.th, ...(currentTier === t ? { background: tierColor(t) + '1f', color: tierColor(t) } : {}) }}>{COMBAT_TIERS[t].name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARISON_ROWS.map((row, i) => (
            <tr key={i}>
              <td style={{ ...cps.td, ...cps.rowLabel }}>{row.label}</td>
              {cols.map((t) => (
                <td key={t} style={{ ...cps.td, ...(currentTier === t ? { background: tierColor(t) + '14' } : {}) }}>{cell(row.vals[t])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export { formatTierPrice, tierRank };
